import { useId } from "react"
import { Box, ButtonBase, Typography } from "@mui/material"
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import PaymentsIcon from '@mui/icons-material/Payments'
import PixIcon from '@mui/icons-material/Pix'
import type { FormaPagamentoLoja } from "@/api"
import { METODOS_PAGAMENTO, type IconePagamento } from "./payment-methods"
import { descreverForma, metodosDoCheckout } from "./passos-pagamento"

const ICONES: Record<IconePagamento | 'dinheiro', JSX.Element> = {
    credito: <CreditCardIcon sx={{ fontSize: 40 }} />,
    debito: <PaymentsIcon sx={{ fontSize: 40 }} />,
    pix: <PixIcon sx={{ fontSize: 40 }} />,
    dinheiro: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
}

interface ListaFormasProps {
    formas: FormaPagamentoLoja[]
    onEscolher: (forma: FormaPagamentoLoja) => void
    colunas: 1 | 2
    /**
     * Com ela o toque só marca (totem de pedidos: quem avança é o "Continuar"). Sem ela, o toque já
     * segue para o pagamento (checkout).
     */
    selecionada?: number | null
    /** Linha a mais no cartão marcado, para o cliente ver o que escolheu nele (ex.: o troco do dinheiro). */
    detalheDaSelecionada?: string | null
}

/** As formas que a loja cadastrou para o totem, em cartões grandes de toque. */
export default function ListaFormas({ formas, onEscolher, colunas, selecionada, detalheDaSelecionada }: ListaFormasProps): JSX.Element {
    const marcando = selecionada !== undefined

    return (
        <Box
            role={marcando ? 'radiogroup' : undefined}
            aria-label={marcando ? 'Forma de pagamento' : undefined}
            sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))`,
                gap: 2.5,
                width: '100%',
                maxWidth: colunas === 2 ? 920 : 650,
            }}
        >
            {formas.map((forma) => (
                <CartaoForma
                    key={forma.id}
                    forma={forma}
                    onEscolher={onEscolher}
                    marcada={marcando ? forma.id === selecionada : undefined}
                    detalheExtra={marcando && forma.id === selecionada ? detalheDaSelecionada : null}
                />
            ))}
        </Box>
    )
}

/** Cor e ícone: na maquininha os mesmos dos botões de antes (um método mostra o dele); no caixa, os da forma. */
function aparencia(forma: FormaPagamentoLoja): { icone: JSX.Element; cor: string } {
    if (forma.tipo === 'TEF') {
        const metodos = metodosDoCheckout(forma)
        const metodo = (metodos.length === 1 ? metodos[0] : undefined) ?? METODOS_PAGAMENTO[0]!

        return { icone: ICONES[metodo.icone], cor: metodo.cor }
    }

    if (forma.forma === 'DINHEIRO') return { icone: ICONES.dinheiro, cor: '#2E7D32' }
    if (forma.forma === 'PIX') return { icone: ICONES.pix, cor: '#00BFA5' }
    if (forma.forma === 'CARTAO') return { icone: ICONES.credito, cor: '#0D47A1' }

    return { icone: ICONES.debito, cor: '#616161' }
}

interface CartaoFormaProps {
    forma: FormaPagamentoLoja
    onEscolher: (forma: FormaPagamentoLoja) => void
    /** `undefined` fora do modo de marcar. */
    marcada?: boolean
    detalheExtra?: string | null
}

function CartaoForma({ forma, onEscolher, marcada, detalheExtra }: CartaoFormaProps): JSX.Element {
    const id = useId()
    const { icone, cor } = aparencia(forma)
    const marcando = marcada !== undefined

    return (
        <ButtonBase
            onClick={() => onEscolher(forma)}
            role={marcando ? 'radio' : undefined}
            aria-checked={marcando ? marcada : undefined}
            aria-labelledby={`${id}-nome`}
            aria-describedby={`${id}-detalhe`}
            sx={{
                width: '100%',
                minHeight: 112,
                p: 2.5,
                gap: 2.5,
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 4,
                border: marcada ? '2px solid' : '1px solid',
                borderColor: marcada ? 'primary.main' : 'divider',
                backgroundColor: 'background.paper',
                transition: 'transform 0.2s ease-in-out, border-color 0.2s ease-in-out',

                '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                },

                '&:active': {
                    transform: 'scale(0.98)',
                },
            }}
        >
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 3,
                    color: '#fff',
                    backgroundColor: cor,
                }}
            >
                {icone}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography id={`${id}-nome`} variant="h4" sx={{ fontWeight: 'bold', overflowWrap: 'anywhere' }}>
                    {forma.descricao}
                </Typography>

                <Typography id={`${id}-detalhe`} variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                    {descreverForma(forma)}
                </Typography>

                {detalheExtra && (
                    <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                        {detalheExtra}
                    </Typography>
                )}
            </Box>

            {!marcando ? (
                <ChevronRightRoundedIcon sx={{ fontSize: 44, color: 'text.secondary', flexShrink: 0 }} />
            ) : marcada ? (
                <CheckCircleRoundedIcon sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />
            ) : (
                <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', flexShrink: 0 }} />
            )}
        </ButtonBase>
    )
}
